/**
 * CardDetailModal — route-based overlay rendered as a child route of BoardViewPage.
 * Composes existing CardDetail, Checklist, Comments, Attachments, and CustomFieldsEditor
 * components with data from ActiveBoardContext and full card detail fetched from the API.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 12.1-12.8
 */

import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useActiveBoard, BoardLabel } from '../state/active-board-context';
import * as api from '../services/api-client';
import { Checklist, ChecklistItem } from '../components/Checklist';
import { Comments, Comment } from '../components/Comments';
import { MarkdownText } from '../components/MarkdownText';
import { Attachments, AttachmentItem } from '../components/Attachments';
import { DownloadConfirmModal, DownloadTarget } from '../components/DownloadConfirmModal';
import { CustomFieldsEditor } from '../components/card/CustomFieldsEditor';
import { CardAssigneePopover } from '../components/card/CardAssigneePopover';
import { LinkedCardsSection, LinkResponseItem } from '../components/card/LinkedCardsSection';
import { SubtasksSection, SubtaskResponseItem } from '../components/card/SubtasksSection';
import { getTokens } from '../services/auth-client';
import { PresenceAvatars } from '../components/PresenceAvatars';

// ---------------------------------------------------------------------------
// Types for full card detail response
// ---------------------------------------------------------------------------

interface CardDetailData {
  readonly cardId: string;
  readonly cardNumber: number;
  readonly title: string;
  readonly description: string;
  readonly listId: string;
  readonly swimlaneId: string;
  readonly labels: { labelId: string; name: string; color: string }[];
  readonly members: { userId: string; displayName: string }[];
  readonly dueDate: string | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly checklists: ChecklistData[];
  readonly comments: Comment[];
  readonly attachments: AttachmentItem[];
  readonly customFields: Record<string, boolean | string | string[]>;
  readonly showDescriptionOnCard?: boolean;
}

interface ChecklistData {
  readonly checklistId: string;
  readonly title: string;
  readonly items: ChecklistItem[];
}

interface AddCommentResponse {
  readonly commentId: string;
  readonly text: string;
  readonly authorId: string;
  readonly authorDisplayName: string;
  readonly createdAt: string;
}

// ---------------------------------------------------------------------------
// Modal state reducer
// ---------------------------------------------------------------------------

interface ModalState {
  readonly cardData: CardDetailData | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

type ModalAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: CardDetailData }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'COMMENT_ADDED'; payload: Comment }
  | { type: 'CARD_UPDATED'; payload: Partial<CardDetailData> }
  | { type: 'CHECKLIST_ITEM_TOGGLED'; payload: { checklistId: string; itemId: string; checked: boolean } }
  | { type: 'CHECKLIST_DELETED'; payload: { checklistId: string } };

const initialState: ModalState = {
  cardData: null,
  isLoading: false,
  error: null,
};

function modalReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SUCCESS':
      return { cardData: action.payload, isLoading: false, error: null };

    case 'FETCH_ERROR':
      return { ...state, isLoading: false, error: action.payload };

    case 'COMMENT_ADDED':
      if (!state.cardData) return state;
      return {
        ...state,
        cardData: {
          ...state.cardData,
          comments: [action.payload, ...state.cardData.comments],
        },
      };

    case 'CARD_UPDATED':
      if (!state.cardData) return state;
      return {
        ...state,
        cardData: { ...state.cardData, ...action.payload },
      };

    case 'CHECKLIST_ITEM_TOGGLED': {
      if (!state.cardData) return state;
      return {
        ...state,
        cardData: {
          ...state.cardData,
          checklists: state.cardData.checklists.map((cl) =>
            cl.checklistId === action.payload.checklistId
              ? {
                  ...cl,
                  items: cl.items.map((item) =>
                    item.itemId === action.payload.itemId
                      ? { ...item, checked: action.payload.checked }
                      : item,
                  ),
                }
              : cl,
          ),
        },
      };
    }

    case 'CHECKLIST_DELETED':
      if (!state.cardData) return state;
      return {
        ...state,
        cardData: {
          ...state.cardData,
          checklists: state.cardData.checklists.filter(
            (cl) => cl.checklistId !== action.payload.checklistId,
          ),
        },
      };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Utility: extract userId from JWT access token payload
// ---------------------------------------------------------------------------

function getCurrentUserId(): string {
  const tokens = getTokens();
  if (!tokens?.accessToken) return 'unknown';
  try {
    const payload = tokens.accessToken.split('.')[1];
    if (!payload) return 'unknown';
    const decoded = JSON.parse(atob(payload));
    return (decoded.sub as string) ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

// ---------------------------------------------------------------------------
// CardDetailModal component
// ---------------------------------------------------------------------------

export function CardDetailModal(): React.ReactElement {
  const { boardId, cardId } = useParams<{ boardId: string; cardId: string }>();
  const navigate = useNavigate();
  const { state: boardState, updateCard, archiveCard, dispatch: boardDispatch } = useActiveBoard();
  const [state, dispatch] = useReducer(modalReducer, initialState);
  const backdropRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [showLabelSelector, setShowLabelSelector] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Links and subtasks state
  const [links, setLinks] = useState<LinkResponseItem[]>([]);
  const [subtasks, setSubtasks] = useState<SubtaskResponseItem[]>([]);
  const [subtaskProgress, setSubtaskProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });

  // Description edit state
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [descSaveStatus, setDescSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const descAutoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus management: store the element that triggered the modal
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // ---------------------------------------------------------------------------
  // Close handler: navigate back to board without unmounting board view
  // ---------------------------------------------------------------------------

  const handleClose = useCallback(() => {
    if (boardId) {
      navigate(`/boards/${boardId}`);
    }
  }, [boardId, navigate]);

  // ---------------------------------------------------------------------------
  // Fetch full card data on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!boardId || !cardId || cardId.startsWith('temp-')) return;

    let cancelled = false;

    async function fetchCardAndAttachments(): Promise<void> {
      dispatch({ type: 'FETCH_START' });
      try {
        const cardResponse = await api.get<{ data: CardDetailData }>(`/v1/boards/${boardId}/cards/${cardId}`);
        if (cancelled) return;

        // Card detail endpoint now includes attachments composed at the BFF layer
        const cardData: CardDetailData = {
          ...cardResponse.data,
          checklists: cardResponse.data.checklists ?? [],
          comments: (cardResponse.data.comments ?? []).map((c: Record<string, unknown>) => ({
            commentId: c.commentId ?? c['commentId'] ?? '',
            authorId: c.authorId ?? c['authorId'] ?? '',
            authorName: c.authorName ?? c['authorDisplayName'] ?? c['authorId'] ?? '',
            authorAvatar: c.authorAvatar ?? '',
            text: c.text ?? '',
            timestamp: c.timestamp ?? c['createdAt'] ?? '',
            editedAt: c.editedAt ?? c['editedAt'] ?? undefined,
            reactions: c.reactions ?? [],
          })),
          attachments: cardResponse.data.attachments ?? [],
          customFields: cardResponse.data.customFields ?? {},
          labels: cardResponse.data.labels ?? [],
          members: cardResponse.data.members ?? [],
        };
        dispatch({ type: 'FETCH_SUCCESS', payload: cardData });
      } catch (error: unknown) {
        if (!cancelled) {
          const message = api.isClientError(error) ? error.message : 'Failed to load card details';
          dispatch({ type: 'FETCH_ERROR', payload: message });
        }
      }
    }

    fetchCardAndAttachments();

    return () => { cancelled = true; };
  }, [boardId, cardId]);

  // ---------------------------------------------------------------------------
  // Fetch links and subtasks on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!boardId || !cardId || cardId.startsWith('temp-')) return;
    let cancelled = false;

    async function fetchLinks(): Promise<void> {
      try {
        const response = await api.get<{ data: LinkResponseItem[] }>(`/v1/boards/${boardId}/cards/${cardId}/links`);
        if (!cancelled) setLinks(response.data ?? []);
      } catch {
        if (!cancelled) setLinks([]);
      }
    }

    async function fetchSubtasks(): Promise<void> {
      try {
        const response = await api.get<{ data: { subtasks: SubtaskResponseItem[]; progress: { completed: number; total: number } } }>(`/v1/boards/${boardId}/cards/${cardId}/subtasks`);
        if (!cancelled) {
          setSubtasks(response.data?.subtasks ?? []);
          setSubtaskProgress(response.data?.progress ?? { completed: 0, total: 0 });
        }
      } catch {
        if (!cancelled) {
          setSubtasks([]);
          setSubtaskProgress({ completed: 0, total: 0 });
        }
      }
    }

    fetchLinks();
    fetchSubtasks();
    return () => { cancelled = true; };
  }, [boardId, cardId]);

  // ---------------------------------------------------------------------------
  // Sync card detail from real-time WebSocket updates
  // When another user updates this card, boardState.cards gets updated via WS.
  // Merge those changes into the local card detail state so the modal stays current.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!cardId || !state.cardData) return;
    const boardCard = boardState.cards.find((c) => c.cardId === cardId);
    if (!boardCard) return;

    // Merge fields that the board-level card summary tracks and that the modal displays
    const updates: Partial<CardDetailData> = {};

    if (boardCard.title !== state.cardData.title) {
      updates.title = boardCard.title;
    }
    if (boardCard.dueDate !== state.cardData.dueDate) {
      updates.dueDate = boardCard.dueDate;
    }
    if (boardCard.listId !== state.cardData.listId) {
      updates.listId = boardCard.listId;
    }
    // Sync custom fields from board-level card into the detail view
    const boardCF = boardCard.customFields ?? {};
    const localCF = state.cardData.customFields ?? {};
    if (JSON.stringify(boardCF) !== JSON.stringify(localCF)) {
      updates.customFields = boardCF;
    }

    if (Object.keys(updates).length > 0) {
      dispatch({ type: 'CARD_UPDATED', payload: updates });
    }
  }, [cardId, boardState.cards, state.cardData]);

  // ---------------------------------------------------------------------------
  // Link and subtask handlers
  // ---------------------------------------------------------------------------

  const handleLinkCreated = useCallback((link: LinkResponseItem) => {
    setLinks((prev) => [...prev, link]);
  }, []);

  const handleLinkRemoved = useCallback(async (linkId: string) => {
    if (!boardId || !cardId) return;
    try {
      await api.del(`/v1/boards/${boardId}/cards/${cardId}/links/${linkId}`);
      setLinks((prev) => prev.filter((l) => l.linkId !== linkId));
    } catch {
      // Deletion failed — link remains
    }
  }, [boardId, cardId]);

  const handleSubtaskCreated = useCallback((subtask: { cardId: string; cardNumber: number; title: string }) => {
    const parentList = boardState.lists.find((l) => l.listId === state.cardData?.listId);
    const newSubtask: SubtaskResponseItem = {
      cardId: subtask.cardId,
      cardNumber: subtask.cardNumber,
      title: subtask.title,
      listId: state.cardData?.listId ?? '',
      listName: parentList?.title ?? '',
      assignees: [],
      isComplete: false,
    };
    setSubtasks((prev) => [...prev, newSubtask]);
    setSubtaskProgress((prev) => ({ completed: prev.completed, total: prev.total + 1 }));
  }, [boardState.lists, state.cardData?.listId]);

  const handleSubtaskToggled = useCallback((subtaskCardId: string, isComplete: boolean) => {
    setSubtasks((prev) => prev.map((s) =>
      s.cardId === subtaskCardId ? { ...s, isComplete } : s,
    ));
    setSubtaskProgress((prev) => ({
      completed: isComplete ? prev.completed + 1 : prev.completed - 1,
      total: prev.total,
    }));
  }, []);

  const handleSubtaskDeleted = useCallback((subtaskCardId: string) => {
    const deletedSubtask = subtasks.find((s) => s.cardId === subtaskCardId);
    setSubtasks((prev) => prev.filter((s) => s.cardId !== subtaskCardId));
    setSubtaskProgress((prev) => ({
      completed: deletedSubtask?.isComplete ? prev.completed - 1 : prev.completed,
      total: prev.total - 1,
    }));
  }, [subtasks]);

  const handleSubtaskRenamed = useCallback(async (subtaskCardId: string, newTitle: string) => {
    try {
      await api.put(`/v1/boards/${boardId}/cards/${subtaskCardId}`, { title: newTitle });
      setSubtasks((prev) => prev.map((s) =>
        s.cardId === subtaskCardId ? { ...s, title: newTitle } : s,
      ));
    } catch {
      // Rename failed
    }
  }, [boardId]);

  const isReadOnly = (() => {
    const uid = getCurrentUserId();
    const userMember = boardState.board?.members.find((m) => m.userId === uid);
    return userMember?.role === 'read-only';
  })();

  const parentLink = links.find((l) => l.linkType === 'parent');

  // ---------------------------------------------------------------------------
  // Focus trap and keyboard handling
  // ---------------------------------------------------------------------------

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Focus the dialog on mount
    const timer = setTimeout(() => {
      const closeBtn = dialogRef.current?.querySelector<HTMLElement>('[data-modal-close]');
      closeBtn?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      // Restore focus on unmount
      previousFocusRef.current?.focus();
    };
  }, []);

  // Escape key to close
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        handleClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  // Focus trap
  useEffect(() => {
    function handleFocusTrap(e: KeyboardEvent): void {
      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0] as HTMLElement | undefined;
      const last = focusable[focusable.length - 1] as HTMLElement | undefined;

      if (!first || !last) return;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleFocusTrap);
    return () => document.removeEventListener('keydown', handleFocusTrap);
  }, []);

  // ---------------------------------------------------------------------------
  // Wire field edits through State_Store → API_Client
  // ---------------------------------------------------------------------------

  const handleFieldEdit = useCallback(async (fields: Partial<CardDetailData>): Promise<void> => {
    if (!boardId || !cardId) return;
    try {
      await api.patch(`/v1/boards/${boardId}/cards/${cardId}`, fields);
      dispatch({ type: 'CARD_UPDATED', payload: fields });
      // Also update the board-level card summary via ActiveBoardContext
      updateCard(boardId, { cardId, ...fields });
    } catch {
      // Field edit failed — could show error but keep modal open
    }
  }, [boardId, cardId, updateCard]);

  // ---------------------------------------------------------------------------
  // Wire comment additions through State_Store → API_Client
  // ---------------------------------------------------------------------------

  const handleAddComment = useCallback(async (text: string): Promise<void> => {
    if (!boardId || !cardId) return;
    try {
      const response = await api.post<{ data: AddCommentResponse }>(
        `/v1/boards/${boardId}/cards/${cardId}/comments`,
        { text },
      );
      const newComment: Comment = {
        commentId: response.data.commentId,
        authorName: response.data.authorDisplayName ?? '',
        authorAvatar: '',
        text: response.data.text,
        timestamp: response.data.createdAt,
        reactions: [],
      };
      dispatch({ type: 'COMMENT_ADDED', payload: newComment });
    } catch {
      // Comment creation failed
    }
  }, [boardId, cardId]);

  const handleDeleteComment = useCallback(async (commentId: string): Promise<void> => {
    if (!boardId || !cardId) return;
    try {
      await api.del(`/v1/boards/${boardId}/cards/${cardId}/comments/${commentId}`);
      // Remove from local state
      if (state.cardData) {
        dispatch({
          type: 'CARD_UPDATED',
          payload: {
            comments: state.cardData.comments.filter((c) => c.commentId !== commentId),
          } as Partial<CardDetailData>,
        });
      }
    } catch {
      // Deletion failed
    }
  }, [boardId, cardId, state.cardData]);

  const handleAddReaction = useCallback(async (commentId: string, emoji: string): Promise<void> => {
    if (!boardId || !cardId) return;
    try {
      await api.post(`/v1/boards/${boardId}/cards/${cardId}/comments/${commentId}/reactions`, { emoji });
    } catch {
      // Reaction failed
    }
  }, [boardId, cardId]);

  const handleEditComment = useCallback(async (commentId: string, newText: string): Promise<void> => {
    if (!boardId || !cardId) return;
    try {
      const response = await api.put<{ data: { commentId: string; text: string; editedAt?: string } }>(
        `/v1/boards/${boardId}/cards/${cardId}/comments/${commentId}`,
        { text: newText },
      );
      if (state.cardData) {
        dispatch({
          type: 'CARD_UPDATED',
          payload: {
            comments: state.cardData.comments.map((c) =>
              c.commentId === commentId
                ? { ...c, text: response.data.text ?? newText, editedAt: response.data.editedAt ?? new Date().toISOString() }
                : c,
            ),
          } as Partial<CardDetailData>,
        });
      }
    } catch {
      // Edit failed
    }
  }, [boardId, cardId, state.cardData]);

  // ---------------------------------------------------------------------------
  // Wire checklist actions
  // ---------------------------------------------------------------------------

  const handleToggleChecklistItem = useCallback(async (
    checklistId: string,
    itemId: string,
    checked: boolean,
  ): Promise<void> => {
    if (!boardId || !cardId) return;
    // Optimistic update
    dispatch({ type: 'CHECKLIST_ITEM_TOGGLED', payload: { checklistId, itemId, checked } });
    try {
      await api.patch(
        `/v1/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/items/${itemId}`,
        { checked },
      );
    } catch {
      // Revert on failure
      dispatch({ type: 'CHECKLIST_ITEM_TOGGLED', payload: { checklistId, itemId, checked: !checked } });
    }
  }, [boardId, cardId]);

  const handleDeleteChecklist = useCallback(async (checklistId: string): Promise<void> => {
    if (!boardId || !cardId) return;
    try {
      await api.del(`/v1/boards/${boardId}/cards/${cardId}/checklists/${checklistId}`);
      dispatch({ type: 'CHECKLIST_DELETED', payload: { checklistId } });
    } catch {
      // Deletion failed
    }
  }, [boardId, cardId]);

  // ---------------------------------------------------------------------------
  // Wire attachment actions
  // ---------------------------------------------------------------------------

  const handleUploadAttachment = useCallback(async (file: File): Promise<void> => {
    if (!boardId || !cardId) return;
    setUploadStatus('uploading');
    setUploadError(null);
    try {
      // Step 1: Request presigned upload URL from BFF
      const response = await api.post<{ data: { attachmentId: string; uploadUrl: string } }>(
        `/v1/boards/${boardId}/cards/${cardId}/attachments`,
        { filename: file.name, mimeType: file.type || 'application/octet-stream', size: file.size },
      );

      const uploadUrl = response.data.uploadUrl;
      if (!uploadUrl) {
        throw new Error('No upload URL received from server');
      }

      // Step 2: Upload file directly to S3 using presigned URL.
      // The presigned URL does not sign content-type, so the browser can
      // freely send it and S3 will use it as the stored object's content type.
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text().catch(() => '');
        throw new Error(`S3 upload failed (${uploadResponse.status}): ${errorText || uploadResponse.statusText}`);
      }

      // Step 3: Refetch attachments to show updated list
      const attachmentsResponse = await api.get<{ data: { attachmentId: string; filename: string; mimeType: string; size: number; uploadedAt: string }[] }>(
        `/v1/boards/${boardId}/cards/${cardId}/attachments`,
      );
      // Normalize attachment items — API may not include isCover field
      const normalizedAttachments = (attachmentsResponse.data ?? []).map((a) => ({
        ...a,
        isCover: false,
      }));
      dispatch({
        type: 'CARD_UPDATED',
        payload: { attachments: normalizedAttachments } as Partial<CardDetailData>,
      });
      setUploadStatus('success');
      setTimeout(() => setUploadStatus('idle'), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setUploadError(message);
      setUploadStatus('error');
    }
  }, [boardId, cardId]);

  const [downloadTarget, setDownloadTarget] = useState<(DownloadTarget & { attachmentId: string }) | null>(null);

  const handleDownloadAttachment = useCallback((attachmentId: string): void => {
    if (!boardId || !cardId) return;
    const attachment = state.cardData?.attachments.find((a) => a.attachmentId === attachmentId);
    setDownloadTarget(attachment ? {
      attachmentId: attachment.attachmentId,
      filename: attachment.filename,
      size: attachment.size,
      mimeType: attachment.mimeType,
    } : { attachmentId, filename: 'attachment', size: undefined, mimeType: undefined });
  }, [boardId, cardId, state.cardData]);

  const handleConfirmDownload = useCallback(async (): Promise<void> => {
    if (!boardId || !cardId || !downloadTarget) return;
    const response = await api.get<{ data: { downloadUrl: string } }>(
      `/v1/boards/${boardId}/cards/${cardId}/attachments/${downloadTarget.attachmentId}/download`,
    );
    const url = response.data.downloadUrl;
    // Use an anchor element to trigger download — avoids pop-up blockers
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadTarget.filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [boardId, cardId, downloadTarget]);

  const handleDeleteAttachment = useCallback(async (attachmentId: string): Promise<void> => {
    if (!boardId || !cardId) return;
    try {
      await api.del(`/v1/boards/${boardId}/cards/${cardId}/attachments/${attachmentId}`);
      if (state.cardData) {
        dispatch({
          type: 'CARD_UPDATED',
          payload: {
            attachments: state.cardData.attachments.filter((a) => a.attachmentId !== attachmentId),
          } as Partial<CardDetailData>,
        });
      }
    } catch {
      // Deletion failed
    }
  }, [boardId, cardId, state.cardData]);

  const handleSetCover = useCallback(async (attachmentId: string): Promise<void> => {
    if (!boardId || !cardId) return;
    try {
      await api.patch(`/v1/boards/${boardId}/cards/${cardId}`, { coverImage: attachmentId });
      if (state.cardData) {
        dispatch({
          type: 'CARD_UPDATED',
          payload: {
            attachments: state.cardData.attachments.map((a) => ({
              ...a,
              isCover: a.attachmentId === attachmentId,
            })),
          } as Partial<CardDetailData>,
        });
      }
    } catch {
      // Set cover failed
    }
  }, [boardId, cardId, state.cardData]);

  // ---------------------------------------------------------------------------
  // Derive list name from board state
  // ---------------------------------------------------------------------------

  const listName = state.cardData
    ? boardState.lists.find((l) => l.listId === state.cardData?.listId)?.title ?? 'Unknown'
    : '';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) {
      handleClose();
    }
  };

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      role="presentation"
      aria-hidden="false"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="card-modal-title"
        className="bg-card text-card-foreground rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-border"
      >
        {/* Loading state */}
        {state.isLoading && (
          <div className="p-6 text-center" role="status" aria-live="polite">
            <p className="text-muted-foreground">Loading card details...</p>
          </div>
        )}

        {/* Error state */}
        {state.error && !state.isLoading && (
          <div className="p-6">
            <p className="text-destructive mb-4" role="alert">{state.error}</p>
            <button
              data-modal-close
              onClick={handleClose}
              className="px-4 py-2 bg-muted text-muted-foreground rounded hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Close
            </button>
          </div>
        )}

        {/* Card detail content */}
        {state.cardData && !state.isLoading && (
          <div className="p-6">
            {/* Header with close button */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="text-xs text-muted-foreground font-mono">#{state.cardData.cardNumber}</span>
                <h2
                  id="card-modal-title"
                  className="text-xl font-semibold text-card-foreground"
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) => {
                    const newTitle = e.currentTarget.textContent?.trim();
                    if (newTitle && newTitle !== state.cardData?.title) {
                      handleFieldEdit({ title: newTitle });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.currentTarget as HTMLElement).blur();
                    }
                  }}
                >
                  {state.cardData.title}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  in list <span className="font-medium">{listName}</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <PresenceAvatars endpoint={`/v1/boards/${boardId}/viewers`} maxVisible={3} />
                <button
                  data-modal-close
                  onClick={handleClose}
                  aria-label="Close card detail"
                className="p-2 rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
              >
                ✕
              </button>
              </div>
            </div>

            {/* Labels with selector */}
            {/* Parent link reference (shown when this card is a subtask) */}
            {parentLink && (
              <div className="mb-4 flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 border border-border">
                <span className="text-xs font-medium text-muted-foreground uppercase">Parent:</span>
                <a
                  href={`/boards/${boardId}/cards/${parentLink.targetCardId}`}
                  className="text-sm font-medium text-primary hover:underline focus:outline-none focus:ring-1 focus:ring-ring rounded-sm px-1"
                  aria-label={`Navigate to parent card: ${parentLink.targetCardTitle}`}
                >
                  {parentLink.targetCardTitle}
                </a>
                <span className="text-xs text-muted-foreground">
                  · {parentLink.targetCardStatus}
                </span>
              </div>
            )}

            {/* Labels with selector */}
            <section aria-label="Labels" className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-medium text-muted-foreground uppercase">Labels</h3>
                <button
                  onClick={() => setShowLabelSelector(!showLabelSelector)}
                  className="text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded px-1"
                  aria-label="Toggle label selector"
                >
                  {showLabelSelector ? 'Done' : 'Edit'}
                </button>
              </div>
              {state.cardData.labels.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {state.cardData.labels.map((label) => (
                    <span
                      key={label.labelId}
                      className="text-xs px-2.5 py-1 rounded text-white font-medium leading-normal"
                      style={{ backgroundColor: label.color }}
                    >
                      {label.name}
                    </span>
                  ))}
                </div>
              )}
              {showLabelSelector && boardState.board && (
                <div className="border border-border rounded-md p-2 bg-muted/50" role="group" aria-label="Board labels">
                  {boardState.board.labels.map((boardLabel: BoardLabel) => {
                    const isActive = state.cardData?.labels.some((l) => l.labelId === boardLabel.labelId) ?? false;
                    return (
                      <button
                        key={boardLabel.labelId}
                        onClick={() => {
                          if (!state.cardData) return;
                          const currentLabels = state.cardData.labels;
                          const newLabels = isActive
                            ? currentLabels.filter((l) => l.labelId !== boardLabel.labelId)
                            : [...currentLabels, { labelId: boardLabel.labelId, name: boardLabel.name, color: boardLabel.color }];
                          handleFieldEdit({ labels: newLabels } as Partial<CardDetailData>);
                        }}
                        className={`flex items-center gap-2 w-full text-left text-sm px-2 py-1 rounded mb-1 ${isActive ? 'bg-primary/10 border border-primary' : 'hover:bg-muted border border-transparent'}`}
                        aria-pressed={isActive}
                      >
                        <span
                          className="w-4 h-4 rounded-sm inline-block"
                          style={{ backgroundColor: boardLabel.color }}
                          aria-hidden="true"
                        />
                        <span className="text-card-foreground">{boardLabel.name}</span>
                        {isActive && <span className="ml-auto text-primary">✓</span>}
                      </button>
                    );
                  })}
                  {boardState.board.labels.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No labels defined for this board</p>
                  )}
                </div>
              )}
            </section>

            {/* Assigned to */}
            <section aria-label="Assigned to" className="mb-4">
              <h3 className="text-xs font-medium text-muted-foreground uppercase mb-1">Assigned to</h3>
              <div className="flex items-center gap-2">
                {state.cardData.members.length > 0 && (
                  <div className="flex items-center gap-2">
                    {(() => {
                      const boardCard = boardState.cards.find((c) => c.cardId === cardId);
                      const avatarUrl = boardCard?.memberAvatars?.[0] ?? null;
                      const name = state.cardData.members[0].displayName;
                      return avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={name}
                          className="h-6 w-6 rounded-full shrink-0"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null;
                    })()}
                    <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-semibold text-primary ${boardState.cards.find((c) => c.cardId === cardId)?.memberAvatars?.[0] ? 'hidden' : ''}`}>
                      {state.cardData.members[0].displayName.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm text-card-foreground">{state.cardData.members[0].displayName}</span>
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => {
                          dispatch({ type: 'CARD_UPDATED', payload: { members: [] } as Partial<CardDetailData> });
                          updateCard(boardId!, { cardId: cardId!, members: [], memberAvatars: [] });
                        }}
                        className="text-muted-foreground hover:text-destructive p-0.5 rounded focus:outline-none focus:ring-2 focus:ring-ring"
                        aria-label={`Remove ${state.cardData.members[0].displayName} from card`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
                          <path d="M5.28 4.22a.75.75 0 0 0-1.06 1.06L6.94 8l-2.72 2.72a.75.75 0 1 0 1.06 1.06L8 9.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L9.06 8l2.72-2.72a.75.75 0 0 0-1.06-1.06L8 6.94 5.28 4.22Z" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
                <CardAssigneePopover
                  boardId={boardId!}
                  cardId={cardId!}
                  boardMembers={(boardState.board?.members ?? []).map((m) => ({
                    userId: m.userId,
                    displayName: m.displayName,
                    email: '',
                  }))}
                  currentAssignees={state.cardData.members}
                  isReadOnly={isReadOnly}
                  onAssignmentChange={(assignees) => {
                    dispatch({ type: 'CARD_UPDATED', payload: { members: assignees } as Partial<CardDetailData> });
                    // Also update board-level card members so MiniCard shows names
                    boardDispatch({ type: 'UPDATE_CARD_OPTIMISTIC', payload: { cardId: cardId!, members: assignees } });
                  }}
                  onAvatarsUpdated={(avatarUrls) => {
                    // Update board-level card summary with signed avatar URLs from the server response
                    boardDispatch({ type: 'UPDATE_CARD_OPTIMISTIC', payload: { cardId: cardId!, memberAvatars: avatarUrls } });
                  }}
                />
              </div>
            </section>

            {/* Start Date with date picker */}
            <section aria-label="Start date" className="mb-4">
              <h3 className="text-xs font-medium text-muted-foreground uppercase mb-1">Start date</h3>
              <input
                type="date"
                value={state.cardData.startDate ? state.cardData.startDate.split('T')[0] : ''}
                onChange={(e) => {
                  const newDate = e.target.value ? new Date(e.target.value).toISOString() : null;
                  handleFieldEdit({ startDate: newDate } as Partial<CardDetailData>);
                }}
                className="border border-border rounded-md px-3 py-1.5 text-sm bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Start date picker"
              />
            </section>

            {/* Due Date with date picker */}
            <section aria-label="Due date" className="mb-4">
              <h3 className="text-xs font-medium text-muted-foreground uppercase mb-1">Due date</h3>
              <input
                type="date"
                value={state.cardData.dueDate ? state.cardData.dueDate.split('T')[0] : ''}
                onChange={(e) => {
                  const newDate = e.target.value ? new Date(e.target.value).toISOString() : null;
                  handleFieldEdit({ dueDate: newDate } as Partial<CardDetailData>);
                }}
                className="border border-border rounded-md px-3 py-1.5 text-sm bg-card text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Due date picker"
              />
            </section>

            {/* Description */}
            <section aria-label="Description" className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xs font-medium text-muted-foreground uppercase">Description</h3>
                {!isReadOnly && (
                  <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer ml-auto">
                    <input
                      type="checkbox"
                      checked={state.cardData.showDescriptionOnCard ?? false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        dispatch({ type: 'CARD_UPDATED', payload: { showDescriptionOnCard: checked } as Partial<CardDetailData> });
                        handleFieldEdit({ showDescriptionOnCard: checked });
                      }}
                      className="rounded border-border text-primary focus:ring-ring h-3.5 w-3.5"
                    />
                    Show on card
                  </label>
                )}
                {editingDescription && descSaveStatus === 'saving' && (
                  <span className="text-xs text-orange-400" aria-live="polite">Auto-saving...</span>
                )}
                {editingDescription && descSaveStatus === 'saved' && (
                  <span className="text-xs text-green-400" aria-live="polite">Saved</span>
                )}
              </div>
              {editingDescription ? (
                <div className="space-y-2">
                  <textarea
                    value={descriptionDraft}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDescriptionDraft(value);
                      setDescSaveStatus('saving');

                      // Debounced auto-save: 1.5s after last keystroke
                      if (descAutoSaveRef.current) {
                        clearTimeout(descAutoSaveRef.current);
                      }
                      descAutoSaveRef.current = setTimeout(() => {
                        const trimmed = value.trim();
                        const current = state.cardData?.description ?? '';
                        if (trimmed !== current) {
                          handleFieldEdit({ description: trimmed });
                        }
                        setDescSaveStatus('saved');
                      }, 1500);
                    }}
                    onBlur={() => {
                      // Save immediately on blur
                      if (descAutoSaveRef.current) {
                        clearTimeout(descAutoSaveRef.current);
                        descAutoSaveRef.current = null;
                      }
                      const trimmed = descriptionDraft.trim();
                      const current = state.cardData?.description ?? '';
                      if (trimmed !== current) {
                        handleFieldEdit({ description: trimmed });
                      }
                      setDescSaveStatus('saved');
                      // Close editor after a short delay so the saved status shows briefly
                      setTimeout(() => {
                        setEditingDescription(false);
                        setDescSaveStatus('idle');
                      }, 400);
                    }}
                    rows={5}
                    autoFocus
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-card text-card-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label="Edit description"
                    placeholder="Add a description... (supports markdown: **bold**, *italic*, `code`, URLs)"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur from firing first
                        if (descAutoSaveRef.current) {
                          clearTimeout(descAutoSaveRef.current);
                          descAutoSaveRef.current = null;
                        }
                        const trimmed = descriptionDraft.trim();
                        const current = state.cardData?.description ?? '';
                        if (trimmed !== current) {
                          handleFieldEdit({ description: trimmed });
                        }
                        setEditingDescription(false);
                        setDescSaveStatus('idle');
                      }}
                      className="bg-primary text-primary-foreground px-3 py-1 rounded-md text-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur from firing first
                        if (descAutoSaveRef.current) {
                          clearTimeout(descAutoSaveRef.current);
                          descAutoSaveRef.current = null;
                        }
                        setEditingDescription(false);
                        setDescSaveStatus('idle');
                      }}
                      className="bg-muted text-muted-foreground px-3 py-1 rounded-md text-sm hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : state.cardData.description ? (
                <div
                  className="text-sm text-card-foreground border border-transparent hover:border-border rounded p-1 cursor-pointer"
                  onClick={() => {
                    setDescriptionDraft(state.cardData?.description ?? '');
                    setEditingDescription(true);
                    setDescSaveStatus('idle');
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setDescriptionDraft(state.cardData?.description ?? '');
                      setEditingDescription(true);
                      setDescSaveStatus('idle');
                    }
                  }}
                  aria-label="Click to edit description"
                >
                  <MarkdownText text={state.cardData.description} />
                </div>
              ) : (
                <p
                  className="text-sm text-muted-foreground italic border border-transparent hover:border-border rounded p-1 cursor-pointer"
                  onClick={() => {
                    setDescriptionDraft('');
                    setEditingDescription(true);
                    setDescSaveStatus('idle');
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setDescriptionDraft('');
                      setEditingDescription(true);
                      setDescSaveStatus('idle');
                    }
                  }}
                >
                  Click to add description
                </p>
              )}
            </section>

            {/* Custom Fields */}
            <CustomFieldsEditor
              boardId={boardId!}
              cardId={cardId!}
              customFields={state.cardData.customFields}
            />

            {/* Checklists */}
            {state.cardData.checklists.length > 0 && (
              <section aria-label="Checklists" className="mb-4">
                <h3 className="text-xs font-medium text-muted-foreground uppercase mb-2">Checklists</h3>
                {state.cardData.checklists.map((checklist) => (
                  <Checklist
                    key={checklist.checklistId}
                    checklistId={checklist.checklistId}
                    title={checklist.title}
                    items={checklist.items}
                    onToggleItem={handleToggleChecklistItem}
                    onDeleteChecklist={handleDeleteChecklist}
                  />
                ))}
              </section>
            )}

            {/* Attachments — always visible for upload capability */}
            <section className="mb-4">
              {/* Subtasks Section */}
              <SubtasksSection
                boardId={boardId!}
                cardId={cardId!}
                subtasks={subtasks}
                progress={subtaskProgress}
                isReadOnly={isReadOnly}
                onSubtaskCreated={handleSubtaskCreated}
                onSubtaskToggled={handleSubtaskToggled}
                onSubtaskDeleted={handleSubtaskDeleted}
                onSubtasksReordered={setSubtasks}
                onSubtaskRenamed={handleSubtaskRenamed}
                parentAssignees={state.cardData?.members ?? []}
                onParentAssigneesChange={async (assignees) => {
                  dispatch({ type: 'CARD_UPDATED', payload: { members: assignees } as Partial<CardDetailData> });
                  try {
                    await api.put(`/v1/boards/${boardId}/cards/${cardId}`, {
                      members: assignees.map((a) => ({ userId: a.userId, displayName: a.displayName })),
                    });
                  } catch {
                    // Revert if API fails — refetch on next open
                  }
                }}
              />
            </section>

            {/* Linked Cards Section */}
            <section className="mb-4">
              <LinkedCardsSection
                boardId={boardId!}
                cardId={cardId!}
                links={links}
                isReadOnly={isReadOnly}
                availableCards={boardState.cards.map((c) => ({ cardId: c.cardId, cardNumber: c.cardNumber, title: c.title }))}
                onLinkCreated={handleLinkCreated}
                onLinkRemoved={handleLinkRemoved}
              />
            </section>

            {/* Attachments — always visible for upload capability */}
            <section className="mb-4">
              {uploadStatus === 'uploading' && (
                <div className="flex items-center gap-2 mb-2 p-2 rounded bg-info/10 text-info text-sm" role="status">
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-info border-t-transparent" />
                  Uploading file...
                </div>
              )}
              {uploadStatus === 'success' && (
                <p className="mb-2 p-2 rounded bg-success/10 text-success text-sm">✓ File uploaded successfully</p>
              )}
              {uploadStatus === 'error' && uploadError && (
                <p className="mb-2 p-2 rounded bg-destructive/10 text-destructive text-sm" role="alert">✗ {uploadError}</p>
              )}
              <Attachments
                attachments={state.cardData.attachments ?? []}
                onUpload={handleUploadAttachment}
                onDownload={handleDownloadAttachment}
                onDelete={handleDeleteAttachment}
                onSetCover={handleSetCover}
              />
            </section>

            {/* Comments */}
            <section className="mt-4">
              <h3 className="text-xs font-medium text-muted-foreground uppercase mb-2">Comments</h3>
              <Comments
                comments={state.cardData.comments}
                currentUserId={getCurrentUserId()}
                onAddComment={handleAddComment}
                onDeleteComment={handleDeleteComment}
                onAddReaction={handleAddReaction}
                onEditComment={handleEditComment}
                mentionableUsers={(boardState.board?.members ?? []).map((m) => ({
                  userId: m.userId,
                  displayName: m.displayName,
                }))}
              />
            </section>

            {/* Archive button */}
            <section className="mt-6 pt-4 border-t border-border">
              <button
                onClick={async () => {
                  if (!boardId || !cardId) return;
                  await archiveCard(boardId, cardId);
                  handleClose();
                }}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md text-sm hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Archive card"
              >
                Archive
              </button>
            </section>
          </div>
        )}
      </div>
      <DownloadConfirmModal
        open={downloadTarget !== null}
        onClose={() => setDownloadTarget(null)}
        onConfirm={handleConfirmDownload}
        target={downloadTarget}
      />
    </div>
  );
}
